<script setup lang="ts">
import { ref, inject, Ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { usePackageManagementStore } from '@/store/PackageManagementStore';
import { useComponentComposable } from '@/components/useComponentComposable';
import { useToast } from 'primevue/usetoast';
import { useDialog } from 'primevue/usedialog';
import Button from 'primevue/button';
import Checkbox from 'primevue/checkbox';
import SeasonRules from '@/components/seasonal/SeasonRules.vue';
import PopupConfirm from '@/components/seasonal/PopupConfirm.vue';

const dialogRef = inject('dialogRef') as Ref;
const onCloseModal = dialogRef.value.options.emits.closeModal;

const { t } = useI18n({ useScope: 'global' });
const dialog = useDialog();
const toast = useToast();
const { convertDateInNextYear, convertDateInYear, checkOverlapDate } = useComponentComposable();
const monthSelected = ref({});
const packageStore = usePackageManagementStore();
const seasonSKCode = ref<string | undefined>('');
const checkErrorRule = ref({});
const matchingIndexes = ref([]);

const ruleCheckedSummer = ref({
  fbr: -1,
  fullRisk: -1,
  preReg: -1,
  sharedRisk: -1
});

const ruleCheckedWinter = ref({
  fbr: -1,
  fullRisk: -1,
  preReg: -1,
  sharedRisk: -1
});

const adjustData = ref({
  summer: [
    {
      startMonth: '',
      startDay: '',
      endMonth: '',
      endDay: '',
      applyRules: {
        fbr: false,
        fullRisk: false,
        preReg: false,
        sharedRisk: false
      }
    }
  ],
  winter: [
    {
      startMonth: '',
      startDay: '',
      endMonth: '',
      endDay: '',
      applyRules: {
        fbr: false,
        fullRisk: false,
        preReg: false,
        sharedRisk: false
      }
    }
  ],
  applyCurrentRule: false
});

const emptyRule = () => {
  return {
    startMonth: '',
    startDay: '',
    endMonth: '',
    endDay: '',
    applyRules: {
      fbr: false,
      fullRisk: false,
      preReg: false,
      sharedRisk: false
    }
  };
};

const AddSummerRule = () => {
  if (adjustData.value.summer.length < 4) {
    adjustData.value.summer.push(emptyRule());
    adjustData.value.summer?.forEach((obj, i) => {
      for (const key in obj.applyRules) {
        if (obj.applyRules[key]) {
          ruleCheckedSummer.value[key] = i;
        }
      }
    });
  }
};

const AddWinterRule = () => {
  if (adjustData.value.winter.length < 4) {
    adjustData.value.winter.push(emptyRule());
    adjustData.value.winter?.forEach((obj, i) => {
      for (const key in obj.applyRules) {
        if (obj.applyRules[key]) {
          ruleCheckedWinter.value[key] = i;
        }
      }
    });
  }
};

const removeSummer = (index: number) => {
  if (adjustData.value.summer.length > 1) {
    ruleCheckedSummer.value = {
      fbr: -1,
      fullRisk: -1,
      preReg: -1,
      sharedRisk: -1
    };
    return adjustData.value.summer.splice(index, 1);
  }
};

const removeWinter = (index: number) => {
  if (adjustData.value.winter.length > 1) {
    ruleCheckedWinter.value = {
      fbr: -1,
      fullRisk: -1,
      preReg: -1,
      sharedRisk: -1
    };
    return adjustData.value.winter.splice(index, 1);
  }
};

const getSkcode = (code: string) => {
  if (code) {
    const parts = code.split('#');
    return parts.length > 1 ? parts[1] : '';
  }
};

const getSeasonalRules = () => {
  packageStore
    .getSeasonalRules()
    .then((res) => {
      if (res && Object.keys(res).length > 0) {
        adjustData.value.summer = res?.summer ? res?.summer : adjustData.value.summer;
        adjustData.value.winter = res?.winter ? res?.winter : adjustData.value.winter;
        adjustData.value.applyCurrentRule = res?.applyCurrentRule ? res?.applyCurrentRule : adjustData.value.applyCurrentRule;
        seasonSKCode.value = getSkcode(res?.sk);
        adjustData.value.summer?.forEach((obj, i) => {
          for (const key in obj.applyRules) {
            if (obj.applyRules[key]) {
              ruleCheckedSummer.value[key] = i;
            }
          }
        });
        adjustData.value.winter?.forEach((obj, i) => {
          for (const key in obj.applyRules) {
            if (obj.applyRules[key]) {
              ruleCheckedWinter.value[key] = i;
            }
          }
        });
      }
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.unexpected_error'), life: 5000 });
    });
};

const submitAdjustSeason = () => {
  const payload = adjustData.value;
  payload.sk = seasonSKCode.value;
  if (!checkAllSeasonRules(adjustData.value?.summer, adjustData.value?.winter)) {
    openErrorSeason();
  } else if (!(checkValidity(adjustData.value?.summer) && checkValidity(adjustData.value?.winter))) {
    toast.add({ severity: 'error', summary: '', detail: t('notification.please_select_date'), life: 5000 });
  } else if (checkMatchRuleSeasonDate()) {
    packageStore
      .editSeasonalRules(payload)
      .then(() => {
        onCloseModal();
        toast.add({ severity: 'success', summary: '', detail: t('notification.submit_success'), life: 3000 });
      })
      .catch(() => {
        toast.add({ severity: 'error', summary: '', detail: t('notification.submit_error'), life: 5000 });
      });
  } else {
    toast.add({ severity: 'error', summary: '', detail: t('management.package.unable_select_overlap_date') });
  }
};

const findDifferences = (array1: Array<any>, array2: Array<any>) => {
  return array2.filter((element) => !array1.includes(element));
};
const checkErrorRules = (arraySeason: Array<any>, seasonType: string) => {
  const falseRuleArray = [];
  const trueRuleArray = [];
  if (arraySeason) {
    for (const obj of arraySeason) {
      const rules = obj.applyRules;
      for (const key in rules) {
        if (rules[key]) {
          trueRuleArray.push(key);
        } else {
          falseRuleArray.push(key);
        }
      }
    }
    const compareArraySummer = [...new Set(falseRuleArray)];
    const diffRulesSummer = findDifferences(trueRuleArray, compareArraySummer);
    if (diffRulesSummer.length) {
      checkErrorRule.value.push({ rules: diffRulesSummer, type: seasonType });
    }
    return trueRuleArray;
  }
};
const checkAllSeasonRules = (summerArr: Array<any>, winterArr: Array<any>) => {
  checkErrorRule.value = [];
  const trueSummerRuleArray = checkErrorRules(summerArr, 'Summer');
  const trueWinterRuleArray = checkErrorRules(winterArr, 'Winter');
  const COMPLETE_NUMBER = 4;
  return trueSummerRuleArray?.length == COMPLETE_NUMBER && trueWinterRuleArray?.length == COMPLETE_NUMBER;
};

const formatString = (inputArray: Array<any>) => {
  const formattedArr = [];
  if (!inputArray[0]?.rules || !inputArray[0]?.rules?.length) {
    return '';
  }
  inputArray?.forEach((season) => {
    formattedArr.push(
      season.rules?.map((word) => {
        switch (word.toLowerCase()) {
          case 'fbr':
            return 'FBR';
          case 'fullrisk':
            return 'Full risk';
          case 'prereg':
            return 'Pre-reg';
          case 'sharedrisk':
            return 'Shared risk';
          default:
            return word;
        }
      })
    );
  });
  const mergedArray = formattedArr?.flatMap((subArray) => subArray);
  const rulesArr = [...new Set(mergedArray)];
  const formattedString = rulesArr.length > 1 ? rulesArr.join(', ') : rulesArr[0].toString();
  return formattedString.replace(/,([^,]*)$/, ' and $1');
};

const formatStringSeason = (inputObject) => {
  if (!Array.isArray(inputObject[0]?.rules) || !inputObject[0]?.rules?.length) {
    return '';
  }
  const seasons = inputObject?.map((item) => item.type).filter((type) => type === 'Summer' || type === 'Winter');
  return seasons.join(', ');
};

const openErrorSeason = () => {
  const dialogRef = dialog.open(PopupConfirm, {
    props: {
      header: t('common.error'),
      modal: true
    },
    data: {
      dialogContent: t('management.package.error_season_rules') + formatString(checkErrorRule.value) + t('management.package.in') + formatStringSeason(checkErrorRule.value) + '.',
      dialogType: 'error'
    },
    emits: {
      onClose: () => {
        dialogRef.close();
        checkErrorRule.value = [];
      }
    }
  });
};

const checkMatchSeason = (begin, target) => {
  let endDate;
  let targetTimeEnd;

  const startDate = convertDateInYear(begin.startMonth, begin.startDay);
  endDate = convertDateInYear(begin.endMonth, begin.endDay);
  if (startDate > endDate) {
    endDate = convertDateInNextYear(begin.endMonth, begin.endDay);
  }
  const targetTimeStart = convertDateInYear(target.startMonth, target.startDay);
  targetTimeEnd = convertDateInYear(target.endMonth, target.endDay);
  if (targetTimeStart > targetTimeEnd) {
    targetTimeEnd = convertDateInNextYear(target.endMonth, target.endDay);
  }
  if (checkOverlapDate(begin, target) || convertDateInNextYear(begin.startMonth, begin.startDay, true) <= targetTimeEnd) {
    return false;
  }
  return !checkDateRange(startDate, endDate, targetTimeStart) && !checkDateRange(startDate, endDate, targetTimeEnd);
};

const checkMatchRuleSeasonDate = () => {
  matchingIndexes.value = [];
  findMatchingRulesIndexes();
  let count = 0;
  let matchSummer = false;
  let matchWinter = false;
  if (matchingIndexes.value?.length > 0) {
    matchingIndexes.value.forEach((match) => {
      if (adjustData.value.summer[match.summerIndex].endDay || adjustData.value.winter[match.winterIndex].endDay) {
        matchSummer = checkMatchSeason(adjustData.value.summer[match.summerIndex], adjustData.value.winter[match.winterIndex]);
        matchWinter = checkMatchSeason(adjustData.value.winter[match.winterIndex], adjustData.value.summer[match.summerIndex]);
        if (!(matchSummer && matchWinter)) {
          return count++;
        }
      }
    });
  }
  return count <= 0;
};

const findMatchingRulesIndexes = () => {
  adjustData.value.summer.forEach((summerItem, summerIndex) => {
    adjustData.value.winter.forEach((winterItem, winterIndex) => {
      const summerRules = summerItem.applyRules;
      const winterRules = winterItem.applyRules;

      const isMatching = Object.keys(summerRules).every((ruleKey) => {
        return summerRules[ruleKey] === winterRules[ruleKey];
      });

      if (isMatching) {
        matchingIndexes.value.push({ summerIndex: summerIndex, winterIndex: winterIndex });
      }
    });
  });
  return matchingIndexes.value;
};

const checkValidity = (arr) => {
  for (const obj of arr) {
    if (obj.startMonth === '' || obj.startDay === '' || obj.endMonth === '' || obj.endDay === '') {
      return false;
    }
  }
  return true;
};

const checkDateRange = (startTime, endTime, targetTime) => {
  return targetTime >= startTime && targetTime <= endTime;
};

const openConfirmSubmit = () => {
  if (checkAllSeasonRules(adjustData.value?.summer, adjustData.value?.winter)) {
    if (adjustData.value.applyCurrentRule) {
      if (checkMatchRuleSeasonDate()) {
        const dialogRef = dialog.open(PopupConfirm, {
          props: {
            header: t('create_quote.confirm'),
            modal: true
          },
          data: {
            dialogContent: t('management.package.confirm_text'),
            dialogType: 'confirm'
          },
          emits: {
            isConfirm: (value) => {
              if (value === true) {
                adjustData.value.applyCurrentRule = true;
                dialogRef.close();
                submitAdjustSeason();
                onCloseModal();
              } else {
                dialogRef.close();
              }
            }
          }
        });
      } else {
        toast.add({ severity: 'error', summary: '', detail: t('management.package.unable_select_overlap_date') });
      }
    } else {
      submitAdjustSeason();
    }
  } else {
    return openErrorSeason();
  }
};

onMounted(() => {
  getSeasonalRules();
});
</script>

<template>
  <div class="season-modal">
    <div class="card flex flex-wrap flex-column justify-content-center card_style" v-for="(summerSeason, index) in adjustData.summer" :key="index" v-bind:id="'summer_' + index">
      <div class="flex justify-content-between align-items-start season-rule">
        <div class="font-bold text-lg">{{ $t('management.package.summer_season') }}</div>
        <i v-if="adjustData?.summer?.length > 1" class="pi pi-times-circle text-primary cursor-pointer text-xl remove_summer" @click="removeSummer(index)"></i>
      </div>
      <SeasonRules :adjustData="adjustData" :ruleChecked="ruleCheckedSummer" :seasonIndex="index" :seasonRule="adjustData.summer" :seasonalData="summerSeason" :seasonType="'summer'" :listMonth="monthSelected"></SeasonRules>
    </div>
    <div class="add_rule rule_summer">
      <p class="cursor-pointer add_btn" :style="{ opacity: adjustData?.summer?.length >= 4 ? 0.5 : 1 }" @click="AddSummerRule()">+ {{ $t('management.package.add_summer_rule') }}</p>
    </div>
    <div class="card flex flex-wrap flex-column justify-content-center" v-for="(winterSeason, index) in adjustData.winter" :key="index" v-bind:id="'winter_' + index">
      <div class="flex justify-content-between align-items-start">
        <div class="font-bold text-lg">{{ $t('management.package.winter_season') }}</div>
        <i v-if="adjustData?.winter?.length > 1" class="pi pi-times-circle text-primary cursor-pointer text-xl remove_winter" @click="removeWinter(index)"></i>
      </div>
      <SeasonRules :adjustData="adjustData" :ruleChecked="ruleCheckedWinter" :seasonIndex="index" :seasonRule="adjustData.winter" :seasonalData="winterSeason" :seasonType="'winter'" :listMonth="monthSelected"></SeasonRules>
    </div>
    <div class="add_rule rule_winter">
      <p class="cursor-pointer add_btn" :style="{ opacity: adjustData?.winter?.length >= 4 ? 0.5 : 1 }" @click="AddWinterRule()">+ {{ $t('management.package.add_winter_rule') }}</p>
    </div>
    <div class="adjust_season_footer">
      <Checkbox v-model="adjustData.applyCurrentRule" :binary="true" :value="adjustData.applyCurrentRule" inputId="currentRule" />
      <label for="currentRule" class="ml-2 text-primary text-lg cursor-pointer">{{ $t('management.package.apply_current_rule') }}</label>
      <div class="flex justify-content-center">
        <Button :label="$t('advance_quote.form.submit')" id="btn_submit" class="mt-3 btn_submit" @click="openConfirmSubmit()" />
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.add_rule {
  color: red;
  text-decoration: underline;
  display: flex;
  align-items: center;
  justify-content: end;
  margin-bottom: 20px;
}
.card_style {
  margin-bottom: 20px;
}
.btn_submit {
  background: #1da750;
  &:hover {
    opacity: 0.9;
    background: #1da750;
    border: 1px solid transparent;
  }
}
</style>
